import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Image as ImageIcon, Trash2, Copy, ExternalLink, Filter } from "lucide-react";
import { api } from "@/lib/api";
import { readApiError } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CATEGORIES = [
  { slug: "", label: "All" },
  { slug: "product", label: "Product" },
  { slug: "hero", label: "Hero" },
  { slug: "category", label: "Category" },
  { slug: "editorial", label: "Editorial" },
  { slug: "library", label: "Library" },
  { slug: "uploaded", label: "Uploaded" },
];

function ImageCard({ img, onDelete, onCopyUrl }) {
  return (
    <div
      data-testid={`library-image-${img.slug}`}
      className="group overflow-hidden rounded-xl border border-[#5E594E] bg-[#5A5347] transition-colors hover:border-[#C9A961]/60"
    >
      <div className="relative aspect-square overflow-hidden bg-[#3D3A33]">
        <img
          src={img.url}
          alt={img.name}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          loading="lazy"
        />
      </div>
      <div className="space-y-1.5 p-3">
        <p className="truncate text-sm text-[#F5EFE2]" title={img.name}>{img.name}</p>
        <div className="flex flex-wrap items-center gap-1">
          <span className="rounded-full border border-[#C9A961]/40 bg-[#C9A961]/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#C9A961]">
            {img.category}
          </span>
          {(img.tags || []).slice(0, 2).map((t) => (
            <span
              key={t}
              className="rounded-full border border-[#5E594E] px-2 py-0.5 text-[10px] text-[#C9BEA8]"
            >
              {t}
            </span>
          ))}
        </div>
        <div className="flex gap-1.5 pt-1">
          <a
            href={img.url}
            target="_blank"
            rel="noreferrer"
            data-testid={`library-open-${img.slug}`}
            className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-[#5E594E] px-2 py-1 text-xs text-[#C9BEA8] transition-colors hover:border-[#C9A961]/60 hover:text-[#C9A961]"
            title="Open full-size"
          >
            <ExternalLink className="h-3 w-3" />
            Open
          </a>
          <button
            type="button"
            onClick={() => onCopyUrl(img)}
            data-testid={`library-copy-${img.slug}`}
            className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-[#5E594E] px-2 py-1 text-xs text-[#C9BEA8] transition-colors hover:border-[#C9A961]/60 hover:text-[#C9A961]"
            title="Copy URL"
          >
            <Copy className="h-3 w-3" />
            Copy
          </button>
          {img.source === "uploaded" && (
            <button
              type="button"
              onClick={() => onDelete(img)}
              data-testid={`library-delete-${img.slug}`}
              className="inline-flex items-center justify-center rounded-md border border-[#5E594E] px-2 py-1 text-xs text-[#C9BEA8] transition-colors hover:border-[#B85A4A]/60 hover:text-[#B85A4A]"
              title="Delete uploaded image"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminLibrary() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = category ? { category } : {};
      const res = await api.get("/admin/library/images", { params });
      setImages(res.data.images || []);
    } catch (e) {
      toast.error("Could not load library", { description: readApiError(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const filtered = useMemo(() => {
    if (!q.trim()) return images;
    const needle = q.trim().toLowerCase();
    return images.filter(
      (i) =>
        i.name?.toLowerCase().includes(needle) ||
        i.slug?.toLowerCase().includes(needle) ||
        (i.tags || []).some((t) => t.toLowerCase().includes(needle))
    );
  }, [q, images]);

  const onCopy = async (img) => {
    try {
      await navigator.clipboard.writeText(img.url);
      toast.success("URL copied", { description: img.name });
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const onDelete = async (img) => {
    if (!window.confirm(`Delete "${img.name}"?`)) return;
    try {
      await api.delete(`/admin/library/images/${img.id}`);
      toast.success("Removed from library");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: readApiError(e) });
    }
  };

  const onAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newUrl.trim()) return;
    setAdding(true);
    try {
      await api.post("/admin/library/images", {
        name: newName.trim(),
        url: newUrl.trim(),
        category: "uploaded",
        tags: ["custom"],
      });
      toast.success("Image added to library");
      setNewName("");
      setNewUrl("");
      load();
    } catch (e) {
      toast.error("Add failed", { description: readApiError(e) });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-library">
      <div>
        <p className="text-xs uppercase tracking-[0.24em] text-[#C9A961]">Brand Library</p>
        <h1 className="admin-h1 mt-2">
          The stockroom.
        </h1>
        <p className="mt-2 text-sm text-[#C9BEA8]">
          Every editorial image on tap. Paste-in your own too.
        </p>
      </div>

      {/* Add-by-URL row */}
      <form
        onSubmit={onAdd}
        data-testid="library-add-form"
        className="rounded-2xl border border-[#5E594E] bg-[#5A5347] p-5"
      >
        <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#9A8E78]">
          Add your own image (by URL)
        </p>
        <div className="grid gap-3 sm:grid-cols-[1fr_1.6fr_auto]">
          <Input
            data-testid="library-add-name"
            placeholder="Name (e.g., Studio bench flatlay)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="border-[#5E594E] bg-[#3D3A33] text-[#F5EFE2] placeholder:text-[#9A8E78]"
          />
          <Input
            data-testid="library-add-url"
            placeholder="https://…/image.png"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            className="border-[#5E594E] bg-[#3D3A33] text-[#F5EFE2] placeholder:text-[#9A8E78]"
          />
          <Button
            type="submit"
            disabled={adding || !newName.trim() || !newUrl.trim()}
            data-testid="library-add-btn"
            className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
          >
            {adding ? "Adding…" : "Add"}
          </Button>
        </div>
      </form>

      {/* Filter + search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-1 text-xs text-[#9A8E78]">
          <Filter className="h-3.5 w-3.5" />
          Filter
        </div>
        <div className="flex flex-wrap gap-1.5">
          {CATEGORIES.map((c) => {
            const active = category === c.slug;
            return (
              <button
                type="button"
                key={c.slug || "all"}
                onClick={() => setCategory(c.slug)}
                data-testid={`library-filter-${c.slug || "all"}`}
                className={[
                  "rounded-full px-3 py-1 text-xs transition-colors",
                  active
                    ? "bg-[#C9A961] text-[#3D3A33]"
                    : "border border-[#5E594E] bg-[#5A5347] text-[#C9BEA8] hover:border-[#C9A961]/60 hover:text-[#C9A961]",
                ].join(" ")}
              >
                {c.label}
              </button>
            );
          })}
        </div>
        <div className="ml-auto w-full max-w-xs">
          <Input
            data-testid="library-search"
            placeholder="Search name, slug, tag…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="border-[#5E594E] bg-[#5A5347] text-[#F5EFE2] placeholder:text-[#9A8E78]"
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center gap-2 py-16 text-[#9A8E78]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading library…
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="library-empty"
          className="flex flex-col items-center gap-2 py-16 text-center text-[#9A8E78]"
        >
          <ImageIcon className="h-8 w-8" />
          <p>No images match those filters.</p>
        </div>
      ) : (
        <div
          data-testid="library-grid"
          className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
        >
          {filtered.map((img) => (
            <ImageCard key={img.id} img={img} onDelete={onDelete} onCopyUrl={onCopy} />
          ))}
        </div>
      )}
      <p className="text-xs text-[#9A8E78]" data-testid="library-count">
        Showing {filtered.length} of {images.length} · seeded 34 editorial images on first startup
      </p>
    </div>
  );
}
